/**
 * ShortcutBrowseItem - Individual shortcut row in the marketplace browse list.
 *
 * Renders differently for custom shortcuts (badge + edit/remove) vs built-in shortcuts (save/remove).
 */

import type { ResolvedShortcut } from '@/components/ui/floating-ask-heidi/shortcut-marketplace-data';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { Bookmark, Trash2, UserRound, Users } from 'lucide-react';
import { memo } from 'react';

export interface ShortcutBrowseItemProps {
  shortcut: ResolvedShortcut;
  isCustom: boolean;
  isPinned: boolean;
  canRemove: boolean;
  isAtShortcutLimit?: boolean;
  maxShortcuts?: number;
  /**
   * When the shortcut is team-scoped (`shortcut.teamShortcutId`), gates Edit/Delete.
   * Non-admin/owner members should see team shortcuts as read-only. Defaults to true
   * so non-team shortcuts and callers that don't care remain unaffected.
   */
  canManageTeamShortcuts?: boolean;
  onSelect: (shortcut: ResolvedShortcut) => void;
  onEdit?: (shortcut: ResolvedShortcut) => void;
  onRemove?: (shortcut: ResolvedShortcut) => void;
}

export const ShortcutBrowseItem = memo(
  ({
    shortcut,
    isCustom,
    isPinned,
    canRemove,
    isAtShortcutLimit = false,
    maxShortcuts = 25,
    canManageTeamShortcuts = true,
    onSelect,
    onEdit,
    onRemove,
  }: ShortcutBrowseItemProps) => {
    const { t } = useTranslations();

    const isTeamShortcut = !!shortcut.teamShortcutId;
    const canMutate = !isTeamShortcut || canManageTeamShortcuts;

    return (
      <div
        data-testid="evidence-shortcut-item"
        data-shortcut-id={shortcut.id}
        data-shortcut-label={shortcut.label}
        data-pinned={isPinned}
        data-custom={isCustom}
        className={cn(
          'group flex items-center justify-between rounded-lg p-3 transition-colors hover:bg-surface-hover',
          !isCustom && isPinned && !canRemove && 'opacity-50'
        )}
      >
        <div className="mr-4 flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-1.5">
            <TypographyP2Bold>{shortcut.label}</TypographyP2Bold>
            {isCustom && shortcut.teamShortcutId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Users className="size-3 shrink-0 text-text-tertiary" />
                </TooltipTrigger>
                <TooltipContent>
                  {canMutate
                    ? t('floatingAskHeidiShortcuts.teamShortcutTooltip')
                    : t('floatingAskHeidiShortcuts.teamShortcutReadOnlyTooltip')}
                </TooltipContent>
              </Tooltip>
            )}
            {isCustom && !shortcut.teamShortcutId && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <UserRound className="size-3 shrink-0 text-text-tertiary" />
                </TooltipTrigger>
                <TooltipContent>
                  {t('floatingAskHeidiShortcuts.customShortcutTooltip')}
                </TooltipContent>
              </Tooltip>
            )}
            {isPinned && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Bookmark className="size-3 shrink-0 fill-current text-text-tertiary" />
                </TooltipTrigger>
                <TooltipContent>
                  {t('floatingAskHeidiShortcuts.savedToShortcuts')}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          {shortcut.description && (
            <TypographyCaption className="line-clamp-2 text-text-secondary">
              {shortcut.description}
            </TypographyCaption>
          )}
        </div>
        {isCustom ? (
          canMutate ? (
            <div className="flex shrink-0 items-center gap-1.5 opacity-0 transition-opacity group-hover:opacity-100">
              <Button
                variant="outline"
                size="xs"
                onClick={() => onEdit?.(shortcut)}
                data-testid="evidence-shortcut-edit"
              >
                {t('floatingAskHeidiShortcuts.editShortcut')}
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="xs"
                    iconOnly
                    onClick={() => onRemove?.(shortcut)}
                    data-testid="evidence-shortcut-delete"
                    aria-label={t('floatingAskHeidiShortcuts.removeShortcutTooltip')}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t('floatingAskHeidiShortcuts.removeShortcutTooltip')}
                </TooltipContent>
              </Tooltip>
            </div>
          ) : null
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  variant="outline"
                  size="xs"
                  className="opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={() => onSelect(shortcut)}
                  disabled={(isPinned && !canRemove) || (!isPinned && isAtShortcutLimit)}
                  data-testid="evidence-shortcut-save-toggle"
                >
                  {isPinned
                    ? t('floatingAskHeidiShortcuts.removeShortcut')
                    : t('floatingAskHeidiShortcuts.saveShortcut')}
                </Button>
              </span>
            </TooltipTrigger>
            {!isPinned && isAtShortcutLimit && (
              <TooltipContent>
                {t('floatingAskHeidiShortcuts.savedShortcutLimitReached', {
                  max: maxShortcuts,
                })}
              </TooltipContent>
            )}
          </Tooltip>
        )}
      </div>
    );
  }
);

ShortcutBrowseItem.displayName = 'ShortcutBrowseItem';
